import { Grid } from '@vapor/material'
import { Currency } from 'src/api/currency'
import ReservationChoice from '../reservation-choice/reservation-choice'
import { useMemo } from 'react'
import { ReservationCartRow, ReservationRate } from '../../form/reservation-new.type'
import { LocalizedString } from 'hic-components'

type ReservationAccordionDetailsProps = {
  resourceTypeId: string
  resourceTypeName: LocalizedString
  resourceTypeCode?: string
  rates: ReservationRate[]
  nights: number
  adults: number
  children: { childId: string; quantity: number }[]
  checkIn: Date
  checkOut: Date
  currency: Currency
  firstInsertedCartRow:
    | {
        rateId: string
        chargeCategoryId: string
      }
    | undefined
  addCartRow: (row: ReservationCartRow, showSuccessMessage: boolean) => void
  navigateToAdditionalServices: () => void
  unavailable: boolean
}

const calculateResourceInfoWeight = (stayIncluded: boolean, breakfastIncluded: boolean, lunchIncluded: boolean, dinnerIncluded: boolean) => {
  return Number(stayIncluded) + Number(breakfastIncluded) * 2 + Number(lunchIncluded) * 3 + Number(dinnerIncluded) * 4
}

export default function ReservationAccordionDetails(props: ReservationAccordionDetailsProps) {
  const resourcesInfo = useMemo(() => {
    return props.rates
      ?.filter((rate) => {
        const index = rate.resourceRates?.findIndex((resRate) => resRate.resourceTypeId === props.resourceTypeId)
        if (index !== undefined && index >= 0) {
          return Boolean(rate.resourceRates?.[index].dailyPrice?.length) && !rate.resourceRates?.[index]?.restrictionsNotMet?.includes('STOPSELL_CHARGE')
        }
        return false
      })
      .map((rate) => {
        return rate.resourceRates
          ?.filter((resRate) => resRate.resourceTypeId === props.resourceTypeId)
          .map((resRate) => {
            return {
              chargeCategoryName: resRate.chargeCategory.name,
              chargeCategoryId: resRate.chargeCategoryId,
              resourceTypeId: resRate.resourceTypeId,
              resourceTypeName: props.resourceTypeName,
              resourceTypeCode: props.resourceTypeCode,
              dailyPrice: resRate.dailyPrice,
              policy: rate.bookingPolicyOverride,
              restrictionsNotMet: resRate.restrictionsNotMet ?? null,
              order: calculateResourceInfoWeight(
                resRate.chargeCategory.stayIncluded,
                resRate.chargeCategory.breakfastIncluded,
                resRate.chargeCategory.lunchIncluded,
                resRate.chargeCategory.dinnerIncluded,
              ),
            }
          })
          .slice()
          .sort((first, second) => {
            return first.order - second.order
          })
      })
  }, [props.rates, props.resourceTypeId, props.resourceTypeName])

  const availableRates = useMemo(() => {
    return props.rates?.filter((rate) => {
      const index = rate.resourceRates?.findIndex((resRate) => resRate.resourceTypeId === props.resourceTypeId)
      if (index !== undefined && index >= 0) {
        return Boolean(rate.resourceRates?.[index].dailyPrice?.length) && !rate.resourceRates?.[index]?.restrictionsNotMet?.includes('STOPSELL_CHARGE')
      }
      return false
    })
  }, [props.rates])

  return (
    <Grid container>
      {availableRates
        .sort((a, b) => Number(a.rate.isAffiliatedCompany) - Number(b.rate.isAffiliatedCompany))
        .map((rate, index) => {
          return (
            <Grid item xs={12} key={'reservationRate' + index}>
              <ReservationChoice
                rateIsAffiliated={rate.rate.isAffiliatedCompany}
                firstInsertedCartRow={props.firstInsertedCartRow}
                rateName={rate.rate.name}
                restrictionsNotMet={rate.restrictionsNotMet}
                cancellationPolicyType={rate.bookingPolicyOverride?.cancellationPolicy.type ?? rate.rate.bookingPolicy.cancellationPolicy.type}
                nights={props.nights}
                resourceRates={resourcesInfo[index]}
                currency={props.currency}
                policyPayment={rate.bookingPolicyOverride?.paymentRule ?? rate.rate.bookingPolicy.paymentRule}
                bookingPolicyName={rate.bookingPolicyOverride?.name ?? rate.rate.bookingPolicy.name}
                closeToArrival={rate.rate.closeToArrival}
                closeToDeparture={rate.rate.closeToDeparture}
                rateId={rate.rate.id}
                start={props.checkIn}
                end={props.checkOut}
                occupancy={{
                  adults: props.adults,
                  children: [
                    ...props.children.map((child) => {
                      return { ...child }
                    }),
                  ],
                }}
                onAddToCart={(reservation, showSuccessMessage) => props.addCartRow(reservation, showSuccessMessage)}
                includedAdditionalServices={rate.rate.mandatoryAdditionalServices ?? []}
                unavailable={props.unavailable}
                minStay={rate.rate.minStay === null ? undefined : rate.rate.minStay}
                maxStay={rate.rate.maxStay === null ? undefined : rate.rate.maxStay}
                navigateToAdditionalServices={props.navigateToAdditionalServices}
              />
            </Grid>
          )
        })}
    </Grid>
  )
}
